import { Button } from 'primereact/button';
import { useEffect, useState, useRef } from 'react';
import { userOption, userResult } from '../../models/orders/order-list-model';
import { Dropdown, DropdownChangeEvent, DropdownFilterEvent } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Calendar, CalendarChangeEvent } from 'primereact/calendar';
import orderServices from '../../services/orders/orderservice';

interface SearchPanelProps {
    onSearch: (filters: {
        startDate?: Date;
        endDate?: Date;
        orderId?: number;
        userId?: number;
    }) => void;
    onReset: () => any;
    setLoading: (loading: boolean) => void;
}

const GenerateInvoiceSearch: React.FC<SearchPanelProps> = ({ onSearch, setLoading, onReset }) => {
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [selectedUser, setSelectedUser] = useState<any>();
    const blankUserDetail: userOption[] = [{ userID: 0, name: '', primaryContactNbr: '' }];
    const [orderUsers, setOrderUsers] = useState<userOption[]>(blankUserDetail);
    const [orderUser, setOrderUser] = useState<userResult[]>([]);
    const [orderNo, setOrderNo] = useState<any>();
    const [resetKey, setResetKey] = useState(0);
    const [dropdownValue, setDropdownValue] = useState<any>();

    const handleDropdownChange = (e: DropdownChangeEvent) => {
        const selectedUserId = e.value;
        const selectedUser = orderUser.find(user => user.userID === selectedUserId);
    
        if (selectedUser) {
            const [name, primaryContactNbr] = selectedUser.detail.split(",").map((item: string) => item.trim());
            setSelectedUser({ name, primaryContactNbr });
            setDropdownValue(selectedUserId);
        }
    };
    
    const handleDropdownFilter = (e: DropdownFilterEvent) => {
        if (e.filter.length > 2) {
            orderServices.getOrderUsers(e.filter).then((response: userOption[]) => {
                if (response.length > 0) {
                    setOrderUsers(response);
                }
            });
        }
    };

    useEffect(() => {
        setOrderUser(orderUsers.map((user) => ({
            userID: user.userID,
            detail: user.userID !== 0 ? `${user.name}, ${user.primaryContactNbr}` : "",
        })));
    }, [orderUsers]);

    const handleSearch = () => {
        setLoading(true);
        const filters: {
            startDate?: Date;
            endDate?: Date;
            orderId?: number;
            userId?: number;
            resetPagination?: boolean;
        } = {
            resetPagination: true,          
        };

        if (startDate) filters.startDate = startDate;
        if (endDate) filters.endDate = endDate;

        if (orderNo) {
            const orderId = parseInt(orderNo);
            if (!isNaN(orderId)) filters.orderId = orderId;
        }

        if (selectedUser) filters.userId = dropdownValue;

        if (Object.keys(filters).length > 0) {
            onSearch(filters);
        } else {
            setLoading(false);
        }
    };

    const handleStartDateChange = (e: CalendarChangeEvent) => {
        setStartDate(e.value instanceof Date ? e.value : null);
    };

    const handleEndDateChange = (e: CalendarChangeEvent) => {
        setEndDate(e.value instanceof Date ? e.value : null);
    };

    const handleReset = () => {
        onReset();
        setOrderUsers(blankUserDetail);
        setSelectedUser(null);
        setStartDate(null);
        setEndDate(null);
        setOrderNo("");
        setResetKey(prev => prev + 1);
        onSearch({
            startDate: undefined,
            endDate: undefined,
            orderId: undefined,
            userId: undefined,      
        });
    };

    return (
        <div className="card p-3 w-full">
            <div className="flex flex-wrap align-items-center gap-3">
                {/* User Search Dropdown */}
                <div className="flex-1 min-w-[200px] relative pt-1">
                    <Dropdown
                        id="SearchUser"
                        name="SearchUser"
                        options={orderUser || []}
                        optionLabel="detail"
                        optionValue="userID"
                        value={dropdownValue}
                        onChange={handleDropdownChange}
                        onFilter={handleDropdownFilter}
                        filter
                        placeholder="Search for User/Mobile"
                        className="w-full"
                    />
                </div>

                {/* Order Number Input */}
                <div className="flex-1 min-w-[150px] relative pt-1">
                    <span className="p-float-label">
                        <InputText
                            id="OrderNo"
                            name="OrderNo"
                            value={orderNo}
                            type="number"
                            onChange={(e) => setOrderNo(e.target.value)}
                            className="w-full"
                        />
                        <label htmlFor="OrderNo">Order No#</label>
                    </span>
                </div>

                {/* Date Range */}
                <div className="flex-1 min-w-[300px] relative pt-1">
                    <div className="flex gap-2">
                        <Calendar
                            key={`start-${resetKey}`}
                            value={startDate}
                            onChange={handleStartDateChange}                           
                            dateFormat="dd/mm/yy"
                            className="flex-1"
                            placeholder="Start date"
                            readOnlyInput
                        />
                        <Calendar
                            key={`end-${resetKey}`}
                            value={endDate}
                            onChange={handleEndDateChange}   
                            dateFormat="dd/mm/yy"                       
                            className="flex-1"
                            placeholder="End date"
                            readOnlyInput
                        />
                    </div>
                </div>

                {/* Buttons */}
                <div className="flex gap-2 relative pt-1">
                    <Button
                        onClick={handleSearch}
                        severity="success"
                        label="Filter"
                    />
                    <Button
                        onClick={handleReset}
                        severity="secondary"
                        text
                        raised
                        label="Reset"
                    />
                </div>
            </div>
        </div>
    );
};

export default GenerateInvoiceSearch;